import React, { useState } from "react";
import emailjs from "emailjs-com";
import { motion } from "framer-motion";

/**
 * Sleek, cool-toned revamp
 * - Minimal glass card over an aurora gradient background
 * - Soft micro-interactions (hover/tap/focus) with Framer Motion
 * - Floating labels, larger hit targets, better a11y
 * - Loading state + success/error feedback
 * - Honeypot anti-spam
 */
export default function ContactForm() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
    company: "", // honeypot (should stay empty)
  });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  function sendEmail(firstName, lastName, msg, email) {
    return emailjs.send(
      "service_fwobm9k",
      "template_m6yebud",
      {
        from_name: firstName + " " + lastName,
        message: msg,
        reply_to: email,
      },
      "vCiDVq8iUC7UWq7lr"
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Block bots: if the hidden field has any value, silently stop
    if (formData.company.trim().length > 0) return;

    setStatus({ state: "loading", message: "Sending…" });
    try {
      await sendEmail(
        formData.firstName,
        formData.lastName,
        formData.message,
        formData.email
      );
      setStatus({ state: "success", message: "Your message has been sent!" });
      setFormData({ firstName: "", lastName: "", email: "", message: "", company: "" });
    } catch (error) {
      console.error("FAILED…", error);
      setStatus({ state: "error", message: "There was an error sending your message." });
    } finally {
      setTimeout(() => setStatus({ state: "idle", message: "" }), 4000);
    }
  };

  const inputBase =
    "peer w-full px-4 py-4 rounded-2xl border border-transparent bg-white/5 text-white/90 placeholder-transparent focus:outline-none focus:border-cyan-300/60 focus:ring-2 focus:ring-cyan-300/40 transition";

  const labelBase =
    "pointer-events-none absolute left-3 top-[4px] px-1 text-sm text-white/60 bg-transparent transition-all duration-200 peer-placeholder-shown:top-[17px] peer-placeholder-shown:text-white/50 peer-focus:top-[4px] peer-focus:text-xs peer-focus:text-cyan-200/90";

  return (
    <section className="relative min-h-[70vh] w-full overflow-hidden">

      <div className="mx-auto max-w-5xl px-4 py-14 md:py-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-5 gap-8 md:gap-10"
        >
          {/* Left: header / copy */}
          <div className="md:col-span-2 flex flex-col justify-center text-center md:text-left mx-8">
            <h1 className="nsans-7-bo text-3xl md:text-4xl tracking-tight text-white">
              Let’s get in touch
            </h1>
            <p className="mt-3 text-white/70 nsans-7-li">
              Feel free to email me at <span className="text-cyan-300">mzhang.labor@gmail.com</span>
              , or drop me a message here. I usually reply within a day.
            </p>
            <ul className="mt-6 space-y-2 text-white/60 text-sm">
            </ul>
          </div>

          {/* Right: glass card form */}
          <motion.form
            onSubmit={handleSubmit}
            className="md:col-span-3 relative mx-4 p-6 md:p-8 rounded-3xl border border-white/10 bg-white/5 backdrop-blur-xl shadow-[0_8px_60px_rgba(0,0,0,0.35)]"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            {/* Decorative top edge glow */}
            <div className="pointer-events-none absolute -inset-px rounded-3xl opacity-60 [mask-image:linear-gradient(to_bottom,black,transparent)] bg-gradient-to-r from-blue-400/30 via-blue-500/20 to-blue-600/30" />

            <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {/* First Name */}
              <div className="relative">
                <input
                  required
                  id="firstName"
                  name="firstName"
                  type="text"
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={inputBase}
                  aria-label="First name"
                  aria-invalid={formData.firstName ? "false" : "true"}
                />
                <label htmlFor="firstName" className={labelBase}>First name</label>
              </div>

              {/* Last Name */}
              <div className="relative">
                <input
                  required
                  id="lastName"
                  name="lastName"
                  type="text"
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={inputBase}
                  aria-label="Last name"
                  aria-invalid={formData.lastName ? "false" : "true"}
                />
                <label htmlFor="lastName" className={labelBase}>Last name</label>
              </div>

              {/* Email */}
              <div className="relative md:col-span-2">
                <input
                  required
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  className={inputBase}
                  aria-label="Email"
                  aria-invalid={formData.email ? "false" : "true"}
                />
                <label htmlFor="email" className={labelBase}>Email</label>
              </div>

              {/* Message */}
              <div className="relative md:col-span-2">
                <textarea
                  required
                  id="message"
                  name="message"
                  placeholder="Your message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={5}
                  className={`${inputBase} resize-y min-h-[140px]`}
                  aria-label="Message"
                />
                <label htmlFor="message" className={labelBase}>Message</label>
              </div>

              {/* Honeypot (hidden from users) */}
              <div className="hidden">
                <label htmlFor="company">Company</label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  value={formData.company}
                  onChange={handleChange}
                  autoComplete="off"
                />
              </div>

              {/* Submit */}
              <motion.button
                type="submit"
                disabled={status.state === "loading"}
                className="md:col-span-2 relative inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 nsans-7-eli text-white shadow-lg shadow-slate-900/50 transition disabled:opacity-60 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300/60 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 hover:from-cyan-400 hover:via-blue-500 hover:to-indigo-500"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {status.state === "loading" ? (
                  <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-80" d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round"/></svg>
                ) : (
                  <>
                    <span>Send message</span>
                  </>
                )}
              </motion.button>
            </div>

            {/* Toast / status */}
            <div className="relative z-10 mt-4 min-h-[1.5rem]" aria-live="polite">
              {status.message && (
                <p
                  className={`${
                    status.state === "success"
                      ? "text-cyan-200"
                      : status.state === "error"
                      ? "text-rose-200"
                      : "text-white/70"
                  } text-sm`}
                >
                  {status.message}
                </p>
              )}
            </div>
          </motion.form>
        </motion.div>
      </div>
    </section>
  );
}
